/**
 * Pan/zoom state for the diagram preview, expressed as a CSS transform
 * (`translate(x, y) scale(scale)`) rather than scene scroll — the preview is a
 * plain DOM node, not the editor canvas, so `scene/zoom.ts` doesn't apply.
 */
export type PreviewTransform = {
  x: number;
  y: number;
  scale: number;
};

export const PREVIEW_MIN_SCALE = 0.2;
export const PREVIEW_MAX_SCALE = 8;

export const PREVIEW_IDENTITY_TRANSFORM: PreviewTransform = {
  x: 0,
  y: 0,
  scale: 1,
};

const clampScale = (scale: number) =>
  Math.min(PREVIEW_MAX_SCALE, Math.max(PREVIEW_MIN_SCALE, scale));

/**
 * Zoom by `factor` while keeping the content under (`pointerX`, `pointerY`)
 * pinned — coordinates are relative to the transform's containing box.
 */
export const zoomPreviewAt = (
  transform: PreviewTransform,
  factor: number,
  pointerX: number,
  pointerY: number,
): PreviewTransform => {
  const scale = clampScale(transform.scale * factor);
  // clamped to the same scale (already at a limit) ⇒ nothing moves
  const ratio = scale / transform.scale;

  return {
    scale,
    x: pointerX - (pointerX - transform.x) * ratio,
    y: pointerY - (pointerY - transform.y) * ratio,
  };
};

export const panPreview = (
  transform: PreviewTransform,
  deltaX: number,
  deltaY: number,
): PreviewTransform => ({
  ...transform,
  x: transform.x + deltaX,
  y: transform.y + deltaY,
});

/** wheel delta → multiplicative zoom factor, capped so one notch is gentle */
export const wheelZoomFactor = (deltaY: number) =>
  Math.exp(-Math.max(-100, Math.min(100, deltaY)) * 0.005);

export const isPreviewTransformed = (transform: PreviewTransform) =>
  transform.x !== 0 || transform.y !== 0 || transform.scale !== 1;
